import React, {useMemo, useState} from 'react';

import springboard from 'springboard';

import {AddTagForm} from './components/add_tag_form';
import {AssignedTags} from './components/assigned_tags';
import {BrowseItemsWithTagsView} from './components/browse_items_with_tags_view';
import {Button} from '@mantine/core';

declare module 'springboard/module_registry/module_registry' {
    interface AllModules {
        Tags: TagsModuleReturnValue;
    }
}

type Numbers = string & { __brand: "Numbers" };
type Letters = string & { __brand: "Letters" };

export type ItemId = Numbers;
export type ItemType = 'project' | 'audio_file';

type TagsModuleReturnValue = {
    getTagsForItem: (itemType: ItemType, itemId: ItemId) => TaggedItem[];
    useTagsForItem: (itemType: ItemType, itemId: ItemId) => TaggedItem[];

    createTagForItem: (itemType: ItemType, itemId: ItemId, tag: string) => TaggedItem;
    removeTagFromItem: (itemType: ItemType, itemId: ItemId, tag: string) => void;

    getExistingTags: () => string[];
    useExistingTags: () => string[];

    getAnyItemsWithTag: (tag: string) => TaggedItem[];
    useAnyItemsWithTag: (tag: string) => TaggedItem[];
};

type TaggedItem = {
    // id: string;
    tag: string;
    item_type: ItemType;
    item_id: ItemId;
};

springboard.registerModule('Tags', {}, async (moduleAPI): Promise<TagsModuleReturnValue> => {
    const allTags = await moduleAPI.statesAPI.createPersistentState<TaggedItem[]>('allTags', []);

    moduleAPI.registerRoute('', {}, () => {
        const [itemId, setItemId] = useState('1' as ItemId);
        const itemType = 'project' as ItemType;

        return (
            <div>
                <Button onClick={() => setItemId('1' as ItemId)}>
                    1
                </Button>
                <Button onClick={() => setItemId('2' as ItemId)}>
                    2
                </Button>
                <span>Selected id: {itemId}</span>

                <AddTagForm
                    itemType={itemType}
                    itemId={itemId}
                />
                <AssignedTags
                    itemType={itemType}
                    itemId={itemId}
                />
            </div>
        );
    });

    moduleAPI.registerRoute('browse', {}, () => {
        return (
            <BrowseItemsWithTagsView/>
        );
    });

    const filterTags = (tags: TaggedItem[], itemType: ItemType, itemId: ItemId) => {
        return tags.filter(t => t.item_type === itemType && t.item_id === itemId);
    }

    const getTagsForItem = (itemType: ItemType, itemId: ItemId) => {
        return filterTags(allTags.getState(), itemType, itemId);
    };

    const useTagsForItem = (itemType: ItemType, itemId: ItemId) => {
        const tags = allTags.useState();

        return useMemo(() => {
            return filterTags(tags, itemType, itemId);
        }, [tags, itemType, itemId]);
    };

    const createTagForItem = (itemType: ItemType, itemId: ItemId, tag: string) => {
        const existingTags = getTagsForItem(itemType, itemId);
        const match = existingTags.find(t => t.tag === tag);
        if (match) {
            return match;
        }

        const taggedItem: TaggedItem = {
            tag,
            item_type: itemType,
            item_id: itemId,
        };

        allTags.setState(tags => [...tags, taggedItem]);
        return taggedItem;
    };

    const removeTagFromItem = (itemType: ItemType, itemId: ItemId, tag: string) => {
        console.log(allTags.getState().length);

        allTags.setState(tags => tags.filter(t => {
            if (t.item_type !== itemType) {
                return true;
            }
            if (t.item_id !== itemId) {
                return true;
            }

            if (t.tag !== tag) {
                return true;
            }

            return false;
        }));

        console.log(allTags.getState().length);
    };

    const extractUniqueTagNames = (tags: TaggedItem[]) => {
        return Array.from(new Set(tags.map(t => t.tag)));
    };

    const getExistingTags = () => {
        return extractUniqueTagNames(allTags.getState());
    };

    const useExistingTags = () => {
        const tags = allTags.useState();

        return useMemo(() => {
            return extractUniqueTagNames(tags);
        }, [tags]);
    };

    const getAnyItemsWithTag = (tag: string) => {
        return allTags.getState().filter(t => t.tag === tag);
    };

    const useAnyItemsWithTag = (tag: string) => {
        const tags = allTags.useState();

        return useMemo(() => {
            if (!tag) {
                const items: TaggedItem[] = [];

                for (const t of tags) {
                    if (!items.find(i => i.item_type === t.item_type && i.item_id === t.item_id)) {
                        items.push(t);
                    }
                }

                return items;
            }

            return tags.filter(t => t.tag === tag);
        }, [tags, tag]);
    };

    return {
        getTagsForItem,
        useTagsForItem,
        createTagForItem,
        removeTagFromItem,
        getExistingTags,
        useExistingTags,
        getAnyItemsWithTag,
        useAnyItemsWithTag,
    };
});
